import { useContext, useEffect, useState } from "react";
import { GridDataStateChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { useTheme } from "@mui/material";
import StyledKendoGrid from "../../../../../../Grid/StyledKendoGrid";
import { CrossingCountryViewModel } from "../../../../../ViewModel/CrossingCountryViewModel";
import CommandColumn from "../../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import InlineEditProps from "../../../../../../Grid/InlineEdit/InlineEditProps";
import { CrossingCountryGridColumns } from "./CrossingCountryGridColumns";
import { EditProjectContext } from "../../../../EditProjectContext";
import { FtlRouteViewModel } from "../../../../../ViewModel/FtlRouteViewModel";
import commandDeleteRow from "../../../../../../Grid/ControlColumn/CommandDeleteRow";
import PageMenuItem from "../../../../../../Layout/PageMenu/PageMenuItem";
import { createUUID } from "../../../../../../Utils/CreateUUID";
import { isProjectReadOnly } from "../../../../IsProjectReadOnly";
import { isCountryRemovable } from "./IsCountryRemovable";
import { ReadOnlyProps } from "../../../../ReadOnlyProps";

const idField = "CrossingCountryId";

type Props = {
  routeId: number;
  crossingCountries: Array<CrossingCountryViewModel>;
};

export const CrossingCountryGrid = ({
  routeId,
  crossingCountries,
  isReadOnly,
}: Props & ReadOnlyProps) => {
  const { project, setProject } = useContext(EditProjectContext);
  const ftlRoute = project.FTLRoutes?.find((f) => f.RouteId === routeId);

  const initialState: State = {};
  const [dataState, setDataState] = useState<State>(initialState);
  const projectReadOnly = isProjectReadOnly(project);
  const [dataResult, setDataResult] = useState<DataResult>({
    data: crossingCountries,
    total: crossingCountries.length,
  });

  useEffect(() => {
    setDataResult({
      data: crossingCountries,
      total: crossingCountries.length,
    });
  }, [crossingCountries]);

  const onDataResultChanged = (updatedDataResult: DataResult) => {
    setDataResult(updatedDataResult);
    setCrossingCountriesForRouteId(routeId, updatedDataResult.data);
  };

  const [editState, setEditState] = useState<number>();
  const inlineEditProps = InlineEditProps({
    idField,
    dataResult,
    onDataReceived: onDataResultChanged,
    setEditState,
    isReadonly: isProjectReadOnly(project),
  });

  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: any) => [
      <CommandDeleteRowButton
        disabled={projectReadOnly || isReadOnly || isCountryRemovable(dataItem, ftlRoute)}
        key="CommandRemoveButton"
        onClick={() =>
          commandDeleteRow({
            idField,
            dataResult,
            setDataResult: onDataResultChanged,
            dataItem,
          })
        }
      />,
    ],
  });

  const addNew = () => {
    const newDataItem = {
      inEdit: true,
      InboundMautKM: 0,
      ReturnMautKM: 0,
      CrossingCountryId: createUUID(),
    };
    setDataResult({
      data: [newDataItem, ...dataResult.data],
      total: dataResult.total + 1,
    });
  };

  const setCrossingCountriesForRouteId = (
    id: number,
    updatedCrossingCountries?: Array<CrossingCountryViewModel>
  ) => {
    function updateFtlRoute(route: FtlRouteViewModel) {
      if (route.RouteId === id) {
        return {
          ...route,
          CrossingCountries: updatedCrossingCountries,
        };
      }

      return route;
    }

    setProject({
      ...project,
      FTLRoutes: project?.FTLRoutes?.map(updateFtlRoute),
    });
  };

  const theme = useTheme();
  return (
    <StyledKendoGrid
      muiTheme={theme}
      resizable
      data={{
        data: dataResult.data.withEditfield(idField, editState),
        total: dataResult.total,
      }}
      dataItemKey="CrossingCountryId"
      {...dataState}
      onDataStateChange={(e: GridDataStateChangeEvent) => {
        setDataState(e.dataState);
      }}
      {...inlineEditProps}
    >
      <GridToolbar>
        <PageMenuItem
          onClick={addNew}
          align="left"
          color="base"
          type="button"
          disabled={projectReadOnly || isReadOnly}
        >
          Add
        </PageMenuItem>
      </GridToolbar>
      {[CrossingCountryGridColumns({ isReadOnly }), commandColumn]}
    </StyledKendoGrid>
  );
};
